import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { AttentionItem } from "../components/AttentionItem";
import { Typography } from "antd";

interface AttentionData {
  label: string;
  description: string;
  impact_area: string;
  evidence: string[];
  urgency: "low" | "normal" | "high" | "critical";
}

// Attention Block Recipe
const attentionRecipe = {
  id: "attention-block",
  name: "Attention Block",
  globalKey: [],
  prefix: `[
        {
          "label": "string — e.g. '🔥 Reopened Ticket', '🚨 Single Point of Failure'",
          "description": "string — what the risk or anomaly is",
          "impact_area": "string — e.g. 'quality', 'workflow', 'ownership'",
          "evidence": ["string — item IDs, metric outliers, contributor info"],
          "urgency": "string — 'low', 'normal', 'high', 'critical'"
        }
      ]`,
  format: {
    type: "json",
    fields: ["label", "description"],
    constraints: [
      { field: "label", type: "string", required: true },
      { field: "description", type: "string", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as AttentionData[];
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (item) => item.label && item.description && item.urgency
            )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// Attention Block Parser
function attentionParser(data: any) {
  try {
    return {
      data: data[attentionBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse attention data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// Attention Block Variants
const attentionVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard attention item display",
  },
  {
    id: "compact",
    name: "Compact",
    description: "Compact attention item display",
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Minimal attention item display",
  },
];

// Attention Block Renderer
function AttentionBlockRenderer(props: any) {
  const { data, variant, settings, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const attentionData = data[attentionBlock.id];

  return (
    <>
      {attentionData?.length > 0 && (
        <div className="vzbl-panel-container">
          <Typography.Title level={4} className="vzbl-header-4">
            Attention Items
          </Typography.Title>
          <div className="responsive-card-grid">
            {attentionData.map((item: AttentionData, idx: number) => (
              <div key={idx} style={{ height: "100%" }}>
                <AttentionItem
                  label={item.label}
                  description={item.description}
                  impactArea={item.impact_area}
                  evidence={item.evidence}
                  urgency={item.urgency}
                  variant={variant}
                  className="vzbl-panel-shadow"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// Create Attention Block
const attentionBlock = createBaseBlock({
  id: "attention",
  name: "Attention Block",
  description: "Displays attention items that need focus",
  recipe: attentionRecipe,
  variants: attentionVariants,
  parser: attentionParser,
  renderer: AttentionBlockRenderer,
});

// Export the block for direct use
export { attentionBlock };
